import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  LinearProgress
} from '@mui/material';
import { materialsAPI, siteAPI } from '../services/api';

const InventoryPage = () => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSites();
  }, []);

  useEffect(() => {
    if (selectedSite) {
      fetchInventory(selectedSite);
    }
  }, [selectedSite]);

  const fetchSites = async () => {
    try {
      const response = await siteAPI.getAll();
      const sitesList = response.data.data.sites;
      setSites(sitesList);
      if (sitesList.length > 0) {
        setSelectedSite(sitesList[0].id);
      }
    } catch (err) {
      setError('Failed to load sites');
      console.error('Error fetching sites:', err);
    }
  };

  const fetchInventory = async (siteId) => {
    try {
      setLoading(true);
      setError('');
      const response = await materialsAPI.getInventory(siteId);
      setInventory(response.data.data.inventory || []);
    } catch (err) {
      setError('Failed to load inventory');
      console.error('Error fetching inventory:', err);
      setInventory([]);
    } finally {
      setLoading(false);
    }
  };

  const getStockLevel = (current, total) => {
    if (!total || total === 0) return 'error';
    const percentage = (current / total) * 100;
    if (percentage > 50) return 'success';
    if (percentage > 20) return 'warning';
    return 'error';
  };

  // Safe calculation with fallback
  const totalSpent = inventory.reduce((sum, item) => {
    return sum + (parseFloat(item.totalSpent) || 0);
  }, 0);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Material Inventory
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Site Selector */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          select
          fullWidth
          label="Select Site"
          value={selectedSite}
          onChange={(e) => setSelectedSite(e.target.value)}
        >
          {sites.map((site) => (
            <MenuItem key={site.id} value={site.id}>
              {site.siteName} - {site.location}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      {/* Summary Cards */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Summary
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Materials
            </Typography>
            <Typography variant="h5">
              {inventory.length}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Spent
            </Typography>
            <Typography variant="h5">
              ₹{totalSpent.toLocaleString()}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Items in Stock
            </Typography>
            <Typography variant="h5">
              {inventory.filter(i => (i.currentStock || 0) > 0).length}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Inventory Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Material</TableCell>
              <TableCell align="right">Received</TableCell>
              <TableCell align="right">Used</TableCell>
              <TableCell align="right">Current Stock</TableCell>
              <TableCell align="right">Total Spent</TableCell>
              <TableCell>Stock Level</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : inventory.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No inventory data available
                </TableCell>
              </TableRow>
            ) : (
              inventory.map((item, index) => {
                const received = parseFloat(item.received) || 0;
                const used = parseFloat(item.used) || 0;
                const currentStock = parseFloat(item.currentStock) || 0;
                const spent = parseFloat(item.totalSpent) || 0;
                const stockPercentage = received > 0 ? ((currentStock / received) * 100) : 0;

                return (
                  <TableRow key={item.categoryId || index}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {item.materialName || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.unit || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {received.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {used.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {currentStock.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      ₹{spent.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {stockPercentage.toFixed(0)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(stockPercentage, 100)}
                          color={getStockLevel(currentStock, received)}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default InventoryPage;